import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { notificationTemplates as initialTemplates } from '../../data/notificationTemplates';

const { FiFileText, FiEdit2, FiCheck, FiX, FiEye, FiTag, FiSave, FiGlobe } = FiIcons;

const VARIABLES = ['{{firstName}}', '{{trainingName}}', '{{dueDate}}', '{{daysLeft}}', '{{daysOverdue}}', '{{managerName}}', '{{completionPct}}', '{{simulationName}}', '{{requirementName}}', '{{requirementType}}', '{{learnerName}}', '{{department}}'];
const categoryColors = { training: 'bg-blue-100 text-blue-700', phishing: 'bg-orange-100 text-orange-700', compliance: 'bg-violet-100 text-violet-700', escalation: 'bg-red-100 text-red-700' };
const langColors = { en: 'bg-slate-100 text-slate-600', es: 'bg-yellow-100 text-yellow-700' };
const langLabels = { en: 'English', es: 'Español' };

const TemplateEditor = () => {
  const [templates, setTemplates] = useState(initialTemplates);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [langFilter, setLangFilter] = useState('all');

  const startEdit = t => { setEditing(t.id); setEditForm({ ...t }); setPreview(null); };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = () => {
    setTemplates(prev => prev.map(t => t.id === editing ? { ...t, ...editForm } : t));
    setEditing(null);
    setEditForm({});
  };

  const getPreviewBody = body => body
    .replace('{{firstName}}', 'Sarah').replace('{{trainingName}}', 'Concienciación de Seguridad Anual / Annual Security Awareness')
    .replace('{{dueDate}}', '2024-02-01').replace('{{daysLeft}}', '7').replace('{{daysOverdue}}', '3')
    .replace('{{managerName}}', 'Tom Harris').replace('{{completionPct}}', '40')
    .replace('{{simulationName}}', 'Urgente: Expiración de Contraseña').replace('{{requirementName}}', 'Capacitación de Seguridad')
    .replace('{{requirementType}}', 'Security Awareness').replace('{{learnerName}}', 'Sarah Jenkins').replace('{{department}}', 'Engineering');

  const visibleTemplates = templates.filter(t => langFilter === 'all' || t.language === langFilter);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <SafeIcon icon={FiTag} className="text-violet-500" />
          <h3 className="text-sm font-semibold text-slate-700">Available Template Variables</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Click to copy. Use these placeholders in your email templates.</p>
        <div className="flex flex-wrap gap-2">
          {VARIABLES.map(v => (
            <button key={v} onClick={() => navigator.clipboard?.writeText(v)} className="px-2.5 py-1 bg-slate-100 hover:bg-blue-100 hover:text-blue-700 text-slate-600 text-xs font-mono rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">{v}</button>
          ))}
        </div>
      </div>

      {/* Language filter */}
      <div className="flex items-center gap-2">
        <SafeIcon icon={FiGlobe} className="text-slate-400" />
        <span className="text-sm font-medium text-slate-600">Filter by language:</span>
        {['all', 'en', 'es'].map(l => (
          <button key={l} onClick={() => setLangFilter(l)} className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${langFilter === l ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {l === 'all' ? 'All' : langLabels[l]}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visibleTemplates.map(t => (
          <div key={t.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-start justify-between p-5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h4 className="text-sm font-semibold text-slate-800">{t.name}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${categoryColors[t.category] || 'bg-slate-100 text-slate-600'}`}>{t.category}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${langColors[t.language] || 'bg-slate-100 text-slate-600'}`}>
                    <SafeIcon icon={FiGlobe} className="text-xs" />{langLabels[t.language] || t.language}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${t.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{t.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <p className="text-xs text-slate-500 font-medium truncate">Subject: {t.subject}</p>
              </div>
              <div className="flex gap-1.5 ml-3 flex-shrink-0">
                <button onClick={() => setPreview(preview === t.id ? null : t.id)} className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"><SafeIcon icon={FiEye} className="text-sm" /></button>
                <button onClick={() => editing === t.id ? cancelEdit() : startEdit(t)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon={editing === t.id ? FiX : FiEdit2} className="text-sm" /></button>
              </div>
            </div>
            <AnimatePresence>
              {preview === t.id && editing !== t.id && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="border-t border-slate-100 p-5 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preview</p>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-100">Subject: {getPreviewBody(t.subject)}</p>
                      <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{getPreviewBody(t.body)}</pre>
                    </div>
                  </div>
                </motion.div>
              )}
              {editing === t.id && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="border-t border-slate-200 p-5 bg-blue-50/40 space-y-4">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Editing Template</p>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Template Name</label>
                      <input value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Subject Line</label>
                      <input value={editForm.subject || ''} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Email Body</label>
                      <textarea value={editForm.body || ''} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))} rows={8} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white resize-none font-mono" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={cancelEdit} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                      <button onClick={saveEdit} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        <SafeIcon icon={FiSave} /> Save Template
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateEditor;